import React, { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  Check,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Database,
  FileCheck2,
  FileText,
  Fingerprint,
  Gamepad2,
  Globe2,
  Layers3,
  Link2,
  LockKeyhole,
  MapPinned,
  Network,
  Radio,
  RefreshCcw,
  Route,
  Scale,
  ScanLine,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Terminal,
  TicketCheck,
  UserCheck,
  WalletCards,
  Waypoints,
  Workflow,
  Zap
} from "lucide-react";

import HeaderToMainPage from "../../Universal/Header/Index";
import Footer from "../../Universal/Footer/Index";
import "./Styles.css";
import SweepstakesHeroCopy3D from "./Helpers/SweepstakesHeroCopy3D.jsx";


/* ============================================================
   PAGE DATA
============================================================ */

const evidencePillars = [
  {
    id: "entry",
    number: "01",
    kicker: "NO-PURCHASE PARTICIPATION",
    title: "A free entry needs a real processing path.",
    short: "Submission source, eligibility review and promotional award records—not a dead-end instruction page.",
    icon: TicketCheck,
    accent: "cyan",
    bullets: ["Record AMOE submissions and intake channel", "Associate each request with the applicable promotion", "Review eligibility and applicable entry limits", "Preserve the decision and resulting award reference"],
    output: "A reviewer can follow a no-purchase submission from receipt through its final disposition."
  },
  {
    id: "currency",
    number: "02",
    kicker: "DUAL-CURRENCY MODEL",
    title: "Play coins and promotional coins serve different purposes.",
    short: "Separate balances, award sources, play activity and redemption eligibility within the operator program.",
    icon: WalletCards,
    accent: "green",
    bullets: ["Non-redeemable entertainment-credit tracking", "Separate promotional-entry balance history", "Origin and purpose recorded for each credit", "No automatic conversion between balance types"],
    output: "Player statements distinguish entertainment activity from promotional credits that may carry prize eligibility."
  },
  {
    id: "redemption",
    number: "03",
    kicker: "PRIZE FULFILLMENT",
    title: "Every redemption deserves a case history.",
    short: "Eligibility checks, manual decisions, provider status and fulfillment references around each prize request.",
    icon: CircleDollarSign,
    accent: "violet",
    bullets: ["Check requested amount and eligible balance", "Capture identity and review requirements", "Track approval, hold, rejection or payout", "Reconcile payment-provider result and case status"],
    output: "An operator can explain why a claim was paid, declined or still awaiting information."
  },
  {
    id: "policy",
    number: "04",
    kicker: "OFFICIAL RULES",
    title: "The published promotion needs an operational counterpart.",
    short: "Eligible markets, entry windows, credit sources and redemption terms translated into configured behavior.",
    icon: BookOpenCheck,
    accent: "blue",
    bullets: ["Promotion dates and eligible participants", "Free-entry and bonus-award treatment", "Currency uses and redemption conditions", "Change history linked to the promotion"],
    output: "Counsel-approved rules can be checked against the behavior of a specific promotion and period."
  },
  {
    id: "fairness",
    number: "05",
    kicker: "GAME-TO-PRIZE LINEAGE",
    title: "Follow promotional play into any resulting prize claim.",
    short: "Gameplay, promotional balance changes and redemption review should share traceable references.",
    icon: Fingerprint,
    accent: "amber",
    bullets: ["Associate promotional play with a game event", "Record debits and credits to the correct balance", "Preserve result and applicable rules context", "Connect eligible winnings to redemption review"],
    output: "A prize claim can be traced to its promotional activity without reopening every vendor dashboard."
  },
  {
    id: "audit",
    number: "06",
    kicker: "COUNSEL & PARTNER REVIEW",
    title: "Prepare a review package around the promotion.",
    short: "Evidence for questions from legal counsel, payment partners, compliance teams and the operator.",
    icon: FileCheck2,
    accent: "pink",
    bullets: ["Entry and balance histories by promotion", "Redemption status and review references", "Applicable rules and policy-change records", "Scoped exports for authorized reviewers"],
    output: "A diligence discussion can start with supporting records and explicit gaps, rather than sales assertions."
  },
];

const lifecycleSteps = [
  {number: "01", label: "INTAKE", title: "Record how the free entry arrived", text: "Keep the submission channel, time, promotion reference and applicant information together.", icon: Radio},
  {number: "02", label: "ASSESS", title: "Apply the approved entry rules", text: "Check the configured eligibility criteria, duplicate handling and submission window.", icon: UserCheck},
  {number: "03", label: "CREDIT", title: "Post an authorized promotional award", text: "An accepted entry can result in a linked promotional-credit event; declined entries retain a reason.", icon: Link2},
  {number: "04", label: "RECONCILE", title: "Answer entry-status questions", text: "Give authorized staff a trace from original request to decision and credit, where applicable.", icon: ClipboardCheck}
];

const redemptionStates = [
  ["REQUESTED", "The player selects a prize request using an eligible promotional balance. Capture amount, time and the applicable terms."],
  ["QUEUED", "Create an operator review case; hold, return or reject requests requiring attention without losing the original submission."],
  ["VERIFY", "Check identity, location, duplicate-account indicators and any documents required for this specific claim."],
  ["APPROVE", "A permitted reviewer accepts or declines the request under the promotion rules, retaining the decision context."],
  ["FULFILL", "Send an approved claim to the agreed payout provider and reconcile its external transaction or failure reference."],
  ["CLOSE", "Archive the outcome with status, reviewer history and ledger links for support, reconciliation and later disputes."]
];

const diligenceQuestions = [
  "Can you trace a free-entry request to the exact promotional credits awarded?",
  "Can a player distinguish entertainment credits from prize-eligible promotional balance?",
  "Which promotions grant credits, and can the operator identify the grant source?",
  "Can a redemption request be held, declined, retried or reconciled with its payout provider?",
  "Can counsel inspect the rules and eligibility conditions applied to a past entry?",
  "Which components are implemented today, and which require operator-specific integration?"
];

const faqItems = [
  { q: "Are Gold Coins and Sweeps Coins the same kind of balance?", a: "No. In a common dual-currency model, Gold Coins-style credits are for entertainment and are not redeemable, while separately defined Sweeps Coins-style promotional credits may be eligible for prizes under the official rules. Ethosino can model distinct value categories and histories. The operator must specify the actual currencies, entitlements, and restrictions in its approved program." },
  { q: "Does buying entertainment credits buy a better chance of winning?", a: "A legitimate sweepstakes entry path must not be conditioned on purchase, and purchase must not improve the chances of winning. How promotional credits are awarded, promoted and used requires counsel-approved program design, clear disclosures and accurate configuration. A dual-currency interface alone does not establish legal compliance." },
  { q: "Can players obtain promotional entries without making a purchase?", a: "The no-purchase route should be clearly disclosed and usable according to the operator’s official rules. Ethosino’s sweepstakes model can support tracking an alternate method of entry (AMOE), eligibility decisions, and any resulting promotional awards. The exact channel, timing, fulfillment requirements, and availability must be confirmed for the deployment." },
  { q: "What happens when a player wants to redeem a prize?", a: "A request should be checked against the promotional balance, eligibility conditions, jurisdiction restrictions, account standing and any identity verification requirement. The operator may need to review the case before a configured payout partner fulfills it. The status history should show what happened even when a request is delayed or declined." },
  { q: "Can promotional credit be moved directly into the entertainment balance?", a: "The product should not assume that two currencies with different rights can be exchanged or merged. Transfers, conversion, bonus grants, expired credits and redemption eligibility must follow explicitly defined rules. The configuration and ledger treatment are reviewed with the operator before enabling a promotional model." },
  { q: "Can free-entry, bonus and game-awarded promotional credits be distinguished?", a: "That distinction is important for support and reconciliation. Records should identify the source of a promotional award—such as an accepted free entry, an eligible promotion or a supported game event—alongside the applicable terms and time. Ask to verify which source fields and reporting views are available in your specific deployment." },
  { q: "Does a dual-currency product make a sweepstakes casino legal in every state?", a: "No. The legality of casino-style sweepstakes is jurisdiction-specific, and restrictions and enforcement actions change. Some states prohibit or materially restrict particular models. Operators need qualified counsel, current jurisdiction decisions and applicable controls; Ethosino is a software provider, not a source of legal authority." },
  { q: "What should we validate before enabling a live promotion?", a: "Review the final official rules, free-entry experience, currency rights, bonus grants, eligible markets, account and age requirements, redemption criteria, provider readiness, support escalation and sample audit records. Confirm which workflows are live, which require integration, and which need operator-side manual processing before any launch." }
];


const revealOrigins = {
  left: { x: -120, y: 0, rotate: -1.5 },
  right: { x: 120, y: 0, rotate: 1.5 },
  topLeft: { x: -135, y: -85, rotate: -2.5 },
  topRight: { x: 135, y: -85, rotate: 2.5 },
  bottomLeft: { x: -135, y: 85, rotate: 2.5 },
  bottomRight: { x: 135, y: 85, rotate: -2.5 },
  up: { x: 0, y: 75, rotate: 0 }
};


const Reveal = ({
  children,
  className = "",
  direction = "up",
  delay = 0,
  amount = 0.16
}) => {
  const reduceMotion = useReducedMotion();
  const origin = revealOrigins[direction] || revealOrigins.up;

  return (
    <motion.div
      className={className}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              x: origin.x,
              y: origin.y,
              rotate: origin.rotate,
              scale: 0.965,
              filter: "blur(10px)"
            }
      }
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1,
        filter: "blur(0px)"
      }}
      viewport={{ once: true, amount }}
      transition={{
        duration: reduceMotion ? 0 : 0.82,
        delay,
        ease: [0.16, 1, 0.3, 1]
      }}
    >
      {children}
    </motion.div>
  );
};


const Spotlight = ({ children, className = "" }) => {
  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--siv6-mx", `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty("--siv6-my", `${event.clientY - rect.top}px`);
  };

  return (
    <div className={`siv6-spotlight ${className}`} onPointerMove={handleMove}>
      <span className="siv6-spotlight__fx" aria-hidden="true" />
      {children}
    </div>
  );
};


/* ============================================================
   PAGE
============================================================ */

const SweepstakesInfrastructurePage = () => {
  const [activePillar, setActivePillar] = useState("currency");
  const [activeFaq, setActiveFaq] = useState(0);
  const [activeState, setActiveState] = useState(0);
  const reduceMotion = useReducedMotion();

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    restDelta: 0.001
  });

  const heroY = useTransform(scrollYProgress, [0, 0.16], [0, reduceMotion ? 0 : 82]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.18], [1, 0.76]);

  const selectedPillar =
    evidencePillars.find((item) => item.id === activePillar) || evidencePillars[0];

  const SelectedPillarIcon = selectedPillar.icon;

  const proofRows = useMemo(
    () => [
      ["tenant", "sample_operator", "EXAMPLE"],
      ["mode", "dual_currency", "CONCEPT"],
      ["free_entry", "intake → decision", "TRACK"],
      ["balances", "play / promotional", "DISTINCT"],
      ["prizes", "request → review", "WORKFLOW"],
      ["rules", "operator-approved", "REQUIRED"]
    ],
    []
  );

  return (
    <Fragment>
      <motion.div className="siv6-scroll-progress" style={{ scaleX: progress }} />

      <HeaderToMainPage />

      <main className="siv6-page siv6-readable">
        {/* ====================================================
            AMBIENT SYSTEM
        ==================================================== */}
        <div className="siv6-atmosphere" aria-hidden="true">
          <div className="siv6-atmosphere__grid" />
          <div className="siv6-atmosphere__noise" />
          <div className="siv6-atmosphere__scan" />
          <div className="siv6-atmosphere__orb siv6-atmosphere__orb--one" />
          <div className="siv6-atmosphere__orb siv6-atmosphere__orb--two" />
          <div className="siv6-atmosphere__orb siv6-atmosphere__orb--three" />
        </div>


        {/* ====================================================
            HERO
        ==================================================== */}
        <section className="siv6-hero" id="top">
          <motion.div className="siv6-hero__parallax" style={{ y: heroY, opacity: heroOpacity }}>
            <div className="siv6-shell siv6-hero__grid">
              <SweepstakesHeroCopy3D reduceMotion={reduceMotion} />


              {/* =================================================
                  HERO OPERATING MAP
              ================================================= */}
              <motion.aside
                className="siv6-hero-system"
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, x: 120, y: -55, rotate: 2.5, scale: 0.94 }
                }
                animate={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
                transition={{ duration: 0.95, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="siv6-hero-system__grid" />
                <div className="siv6-hero-system__scan" />

                <div className="siv6-hero-system__topbar">
                  <span><Radio size={13} /> PROMOTION / EXAMPLE FLOW</span>
                  <b><i /> ILLUSTRATIVE</b>
                </div>

                <div className="siv6-system-map">
                  <motion.div
                    className="siv6-system-orbit siv6-system-orbit--outer"
                    animate={reduceMotion ? {} : { rotate: 360 }}
                    transition={{ duration: 32, ease: "linear", repeat: Infinity }}
                  />
                  <motion.div
                    className="siv6-system-orbit siv6-system-orbit--inner"
                    animate={reduceMotion ? {} : { rotate: -360 }}
                    transition={{ duration: 21, ease: "linear", repeat: Infinity }}
                  />

                  <div className="siv6-system-core">
                    <span className="siv6-system-core__pulse" />
                    <Workflow size={28} />
                    <small>ETHOSINO</small>
                    <strong>PROGRAM MODEL</strong>
                    <b>TENANT AWARE</b>
                  </div>

                  {[
                    ["AMOE", TicketCheck, "cyan", "top"],
                    ["LEDGER", WalletCards, "green", "rightTop"],
                    ["REDEEM", CircleDollarSign, "violet", "rightBottom"],
                    ["RULESET", BookOpenCheck, "blue", "bottom"],
                    ["FAIRNESS", Fingerprint, "amber", "leftBottom"],
                    ["AUDIT", FileCheck2, "pink", "leftTop"]
                  ].map(([label, Icon, accent, position], index) => (
                    <motion.div
                      key={label}
                      className={`siv6-system-node siv6-system-node--${accent} siv6-system-node--${position}`}
                      animate={
                        reduceMotion
                          ? {}
                          : {
                              y: [0, index % 2 === 0 ? -7 : 7, 0],
                              x: [0, index % 3 === 0 ? 4 : -3, 0]
                            }
                      }
                      transition={{ duration: 4 + index * 0.31, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Icon size={16} />
                      <span>{label}</span>
                    </motion.div>
                  ))}

                  <span className="siv6-system-packet siv6-system-packet--one" />
                  <span className="siv6-system-packet siv6-system-packet--two" />
                  <span className="siv6-system-packet siv6-system-packet--three" />
                </div>

                <div className="siv6-runtime-terminal">
                  <div className="siv6-runtime-terminal__head">
                    <Terminal size={13} />
                    illustrative.promotion.flow
                    <span>EXAMPLE</span>
                  </div>

                  <div className="siv6-runtime-terminal__rows">
                    {proofRows.map(([key, value, status]) => (
                      <div key={key}>
                        <span>{key}</span>
                        <code>{value}</code>
                        <b>{status}</b>
                      </div>
                    ))}
                  </div>
                </div>

                <motion.div
                  className="siv6-float-proof siv6-float-proof--left"
                  animate={reduceMotion ? {} : { x: [0, -8, 0], y: [0, 5, 0] }}
                  transition={{ duration: 5.3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Clock3 size={15} />
                  <div><small>RULESET</small><strong>OPERATOR-DEFINED</strong></div>
                </motion.div>

                <motion.div
                  className="siv6-float-proof siv6-float-proof--right"
                  animate={reduceMotion ? {} : { x: [0, 8, 0], y: [0, -5, 0] }}
                  transition={{ duration: 4.9, repeat: Infinity, ease: "easeInOut" }}
                >
                  <BadgeCheck size={15} />
                  <div><small>EVIDENCE</small><strong>REVIEWABLE PATH</strong></div>
                </motion.div>
              </motion.aside>
            </div>
          </motion.div>

          <div className="siv6-manifesto-rail">
            <div className="siv6-manifesto-track">
              {[...[
                "GOLD COINS: ENTERTAINMENT PLAY.",
                "SWEEPS COINS: PROMOTIONAL RULES.",
                "NO PURCHASE REQUIRED TO ENTER.",
                "A PRIZE CLAIM HAS A STATUS.",
                "CLEAR RULES FOR EVERY PROMOTION.",
                "RECORD THE SOURCE OF EACH CREDIT."
              ], ...[
                "GOLD COINS: ENTERTAINMENT PLAY.",
                "SWEEPS COINS: PROMOTIONAL RULES.",
                "NO PURCHASE REQUIRED TO ENTER.",
                "A PRIZE CLAIM HAS A STATUS.",
                "CLEAR RULES FOR EVERY PROMOTION.",
                "RECORD THE SOURCE OF EACH CREDIT."
              ]].map((line, index) => (
                <React.Fragment key={`${line}-${index}`}>
                  <span>{line}</span>
                  <i>◆</i>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>


        {/* ====================================================
            STICKY SUBNAV
        ==================================================== */}
        <nav className="siv6-subnav" aria-label="Sweepstakes infrastructure navigation">
          <div className="siv6-shell siv6-subnav__inner">
            <div className="siv6-subnav__brand"><i /> SWEEPSTAKES OPERATIONS</div>
            <div className="siv6-subnav__links">
              <a href="#model">Model</a>
              <a href="#evidence-system">Components</a>
              <a href="#currency">Dual currency</a>
              <a href="#amoe">AMOE</a>
              <a href="#redemption">Redemption</a>
              <a href="#fairness">Game linkage</a>
              <a href="#boundary">Responsibilities</a>
              <Link to="/book-demo">Book demo</Link>
            </div>
          </div>
        </nav>


        {/* ====================================================
            MODEL / POSITIONING
        ==================================================== */}
        <section className="siv6-section" id="model">
          <div className="siv6-shell">
            <Reveal direction="topLeft" className="siv6-section-head siv6-section-head--split">
              <div>
                <span className="siv6-kicker">DUAL-CURRENCY MODEL IN PRACTICE</span>
                <h2>
                  Not every coin has the same purpose.
                   <span> Not every balance has the same rights.</span>
                </h2>
              </div>
              <p>
                A common U.S. sweepstakes-style experience uses play-only Gold Coins and separately governed promotional Sweeps Coins. The operator's official rules determine how credits are received, used, earned and—where eligible—redeemed for prizes. Ethosino's role is to keep those differences visible in the player experience and accountable in the underlying records.
              </p>
            </Reveal>

            <div className="siv6-model-grid">
              <Reveal direction="bottomLeft" className="siv6-model-manifesto">
                <Spotlight className="siv6-card siv6-card--manifesto">
                  <div className="siv6-card__mesh" />
                  <span className="siv6-kicker">THE PLAYER EXPERIENCE</span>
                  <h3>Players need to understand which balance they are using.</h3>
                  <p>
                    Before a game starts, a player should know whether they are using entertainment-only credits or promotional credits. Account history should identify how credits were awarded and spent. A prize request should have clear eligibility requirements and an understandable status—without presenting promotional credits as the same thing as cash.
                  </p>
                  <div className="siv6-model-chain">
                    {["DISCLOSE", "CREDIT", "PLAY", "REVIEW"].map((item, index) => (
                      <React.Fragment key={item}>
                        <div><small>0{index + 1}</small><strong>{item}</strong></div>
                        {index < 3 ? <ArrowRight size={16} /> : null}
                      </React.Fragment>
                    ))}
                  </div>
                </Spotlight>
              </Reveal>

              <div className="siv6-model-side">
                <Reveal direction="topRight">
                  <Spotlight className="siv6-card siv6-model-card">
                    <Scale size={21} />
                    <span>OFFICIAL RULES</span>
                    <h3>Counsel defines the permissible model.</h3>
                    <p>
                      Program rules establish the eligible audience, free-entry method, currency rights, prize terms and any jurisdiction-specific restrictions.
                    </p>
                  </Spotlight>
                </Reveal>

                <Reveal direction="bottomRight" delay={0.08}>
                  <Spotlight className="siv6-card siv6-model-card">
                    <Workflow size={21} />
                    <span>PRODUCT CONFIGURATION</span>
                    <h3>The product must reflect those decisions.</h3>
                    <p>
                      The player lobby, promotional balances, entry processing and prize workflow are configured around the agreed program rather than one universal sweepstakes template.
                    </p>
                  </Spotlight>
                </Reveal>
              </div>
            </div>
          </div>
        </section>


        {/* ====================================================
            INTERACTIVE EVIDENCE SYSTEM
        ==================================================== */}
        <section className="siv6-section siv6-section--panel" id="evidence-system">
          <div className="siv6-shell">
            <Reveal direction="topRight" className="siv6-section-head siv6-section-head--center">
              <span className="siv6-kicker">SIX PROGRAM COMPONENTS</span>
              <h2>A promotion is defined by <span>what players can actually do.</span></h2>
              <p>
                Explore the decisions that shape a sweepstakes deployment: how free participation works, what each coin represents, how prizes are fulfilled, which rules apply, and which records connect the experience. Each component can be scoped to the operator's approved program.
              </p>
            </Reveal>

            <div className="siv6-evidence-explorer">
              <Reveal direction="left" className="siv6-evidence-nav">
                {evidencePillars.map((pillar) => {
                  const Icon = pillar.icon;
                  const active = pillar.id === activePillar;

                  return (
                    <motion.button
                      key={pillar.id}
                      type="button"
                      className={`siv6-evidence-tab siv6-evidence-tab--${pillar.accent} ${active ? "is-active" : ""}`}
                      onClick={() => setActivePillar(pillar.id)}
                      whileHover={{ x: 5 }}
                      whileTap={{ scale: 0.985 }}
                    >
                      <span className="siv6-evidence-tab__number">{pillar.number}</span>
                      <span className="siv6-evidence-tab__icon"><Icon size={17} /></span>
                      <span className="siv6-evidence-tab__copy">
                        <small>{pillar.kicker}</small>
                        <strong>{pillar.title}</strong>
                      </span>
                      <ArrowRight size={15} />
                    </motion.button>
                  );
                })}
              </Reveal>

              <Reveal direction="right" className="siv6-evidence-detail">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedPillar.id}
                    className={`siv6-evidence-detail__inner siv6-evidence-detail__inner--${selectedPillar.accent}`}
                    initial={
                      reduceMotion
                        ? false
                        : { opacity: 0, x: 80, y: -22, rotate: 1.2, filter: "blur(8px)" }
                    }
                    animate={{ opacity: 1, x: 0, y: 0, rotate: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, x: -60, y: 20, rotate: -1.2, filter: "blur(6px)" }}
                    transition={{ duration: 0.38 }}
                  >
                    <div className="siv6-evidence-detail__top">
                      <div className="siv6-evidence-detail__icon"><SelectedPillarIcon size={26} /></div>
                      <div><span>{selectedPillar.number} / 06 • {selectedPillar.kicker}</span><h3>{selectedPillar.title}</h3></div>
                    </div>

                    <p className="siv6-evidence-detail__short">{selectedPillar.short}</p>

                    <div className="siv6-evidence-capabilities">
                      {selectedPillar.bullets.map((bullet) => (
                        <div key={bullet}><span><Check size={12} /></span>{bullet}</div>
                      ))}
                    </div>

                    <div className="siv6-evidence-output">
                      <Route size={18} />
                      <div><span>OPERATOR RESULT</span><p>{selectedPillar.output}</p></div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>


        {/* ====================================================
            DUAL CURRENCY
        ==================================================== */}
        <section className="siv6-section" id="currency">
          <div className="siv6-shell">
            <Reveal direction="bottomLeft" className="siv6-section-head siv6-section-head--split">
              <div>
                <span className="siv6-kicker">GOLD COINS / SWEEPS COINS</span>
                <h2>Two credit types. <span>Two clearly defined uses.</span></h2>
              </div>
              <p>
                In a typical two-coin experience, Gold Coins are for non-redeemable entertainment, while Sweeps Coins represent promotional play that may support prize claims under published conditions. These names are examples, not a guarantee of legal treatment. Players need separate balances, clear disclosures and a history of how each credit was received or used.
              </p>
            </Reveal>

            <div className="siv6-currency-layout">
              <Reveal direction="topLeft" className="siv6-currency-copy">
                <Spotlight className="siv6-card siv6-currency-thesis">
                  <span className="siv6-kicker">PLAYER BALANCE CLARITY</span>
                  <h3>Never make players guess what a coin is worth.</h3>
                  <p>
                    A purchase of entertainment credits, a free promotional award and a game-generated promotional credit are not the same event. Ethosino's dual-balance model can keep the source and effect distinct, so the cashier, activity history and prize-request workflow present the correct context.
                  </p>

                  <div className="siv6-currency-questions">
                    {["WHICH COIN?", "WHERE FROM?", "WHAT CAN IT DO?", "PRIZE ELIGIBLE?"].map((q, index) => (
                      <div key={q}><small>0{index + 1}</small><strong>{q}</strong></div>
                    ))}
                  </div>
                </Spotlight>
              </Reveal>

              <Reveal direction="bottomRight" className="siv6-ledger-visual">
                <div className="siv6-ledger-visual__grid" />
                <div className="siv6-ledger-visual__head">
                  <span><Database size={15} /> TWO-BALANCE EXAMPLE</span>
                  <b>DISTINCT PURPOSES</b>
                </div>

                <div className="siv6-ledger-streams">
                  <div className="siv6-ledger-stream siv6-ledger-stream--play">
                    <div className="siv6-ledger-stream__icon"><Gamepad2 size={22} /></div>
                    <span>ENTERTAINMENT</span>
                    <strong>GOLD COINS</strong>
                    <small>Play-only credits; no prize redemption in this illustrative model.</small>
                    <div className="siv6-ledger-events"><i /><i /><i /><i /></div>
                  </div>

                  <div className="siv6-ledger-separator">
                    <LockKeyhole size={17} />
                    <span>SEPARATE</span>
                  </div>

                  <div className="siv6-ledger-stream siv6-ledger-stream--promo">
                    <div className="siv6-ledger-stream__icon"><Sparkles size={22} /></div>
                    <span>PROMOTIONAL</span>
                    <strong>SWEEPS COINS</strong>
                    <small>Promotional credits with rules-based prize eligibility, where permitted.</small>
                    <div className="siv6-ledger-events"><i /><i /><i /><i /></div>
                  </div>
                </div>

                <div className="siv6-ledger-foot">
                  <div><span>TENANT</span><b>example_brand</b></div>
                  <div><span>RULESET</span><b>program:sample</b></div>
                  <div><span>EXPORT</span><b>BY SCOPE</b></div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>


        {/* ====================================================
            AMOE
        ==================================================== */}
        <section className="siv6-section siv6-section--panel" id="amoe">
          <div className="siv6-shell">
            <Reveal direction="topRight" className="siv6-section-head siv6-section-head--split">
              <div>
                <span className="siv6-kicker">NO-PURCHASE ENTRY / AMOE</span>
                <h2>Free participation should be <span>visible and usable.</span></h2>
              </div>
              <p>
                An alternate method of entry (AMOE) is not a substitute for clear instructions or a functioning process. The operator sets the acceptable submission channels and conditions with counsel. The software and staff workflow should then show when a request was received, what review occurred and whether promotional credits were awarded.
              </p>
            </Reveal>

            <div className="siv6-amoe-timeline">
              <div className="siv6-amoe-timeline__rail" />
              {lifecycleSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <Reveal
                    key={step.number}
                    direction={index % 2 === 0 ? "bottomLeft" : "topRight"}
                    delay={index * 0.06}
                    className={`siv6-amoe-step siv6-amoe-step--${index + 1}`}
                  >
                    <Spotlight className="siv6-card siv6-amoe-step__card">
                      <div className="siv6-amoe-step__index">{step.number}</div>
                      <div className="siv6-amoe-step__icon"><Icon size={19} /></div>
                      <span>{step.label}</span>
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </Spotlight>
                  </Reveal>
                );
              })}
              <motion.span
                className="siv6-amoe-packet"
                animate={reduceMotion ? {} : { left: ["2%", "96%"] }}
                transition={{ duration: 5.4, repeat: Infinity, ease: "linear" }}
              />
            </div>

            <Reveal direction="bottomRight" className="siv6-diligence-callout">
              <div className="siv6-diligence-callout__icon"><ShieldAlert size={22} /></div>
              <div>
                <span>PLAYER SUPPORT TEST</span>
                <h3>Can a player find out whether their free-entry request was received?</h3>
                <p>
                  A useful response has a submission reference, recorded status and next step—not an instruction to try again without context. Entry methods and processing requirements vary by the operator's approved rules.
                </p>
              </div>
            </Reveal>
          </div>
        </section>


        {/* ====================================================
            REDEMPTION
        ==================================================== */}
        <section className="siv6-section" id="redemption">
          <div className="siv6-shell">
            <Reveal direction="topLeft" className="siv6-section-head siv6-section-head--split">
              <div>
                <span className="siv6-kicker">PROMOTIONAL PRIZE CLAIMS</span>
                <h2>Winning promotional play is not <span>the final payout step.</span></h2>
              </div>
              <p>
                A player may need to meet the promotion's redemption conditions before receiving a prize. Identity verification, account review, payout availability and manual approval can affect the outcome. Ethosino's workflow model provides visible stages so teams can explain whether a request is pending, held, rejected or fulfilled.
              </p>
            </Reveal>

            <div className="siv6-redemption-grid">
              <Reveal direction="bottomLeft" className="siv6-redemption-state-machine">
                <div className="siv6-redemption-state-machine__head">
                  <div><span>ILLUSTRATIVE PRIZE FLOW</span><strong>SELECT A CASE STATUS</strong></div>
                  <b>{String(activeState + 1).padStart(2, "0")} / 06</b>
                </div>

                <div className="siv6-redemption-state-list">
                  {redemptionStates.map(([state, description], index) => (
                    <motion.button
                      type="button"
                      key={state}
                      className={activeState === index ? "is-active" : ""}
                      onClick={() => setActiveState(index)}
                      whileHover={{ x: 5 }}
                    >
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{state}</strong>
                      <ArrowRight size={14} />
                    </motion.button>
                  ))}
                </div>
              </Reveal>

              <Reveal direction="topRight" className="siv6-redemption-detail">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={redemptionStates[activeState][0]}
                    initial={reduceMotion ? false : { opacity: 0, x: 80, y: -30, filter: "blur(8px)" }}
                    animate={{ opacity: 1, x: 0, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, x: -60, y: 25, filter: "blur(6px)" }}
                    transition={{ duration: 0.36 }}
                  >
                    <div className="siv6-redemption-detail__icon"><CircleDollarSign size={27} /></div>
                    <span>CASE STATUS</span>
                    <h3>{redemptionStates[activeState][0]}</h3>
                    <p>{redemptionStates[activeState][1]}</p>

                    <div className="siv6-redemption-evidence">
                      <div><small>REVIEWER</small><strong>IF REQUIRED</strong></div>
                      <div><small>TERMS</small><strong>APPLICABLE</strong></div>
                      <div><small>STATUS</small><strong>TRACKED</strong></div>
                    </div>

                    <div className="siv6-redemption-chain">
                      <span>CLAIM</span><ArrowRight size={13} />
                       <span>ELIGIBILITY</span><ArrowRight size={13} />
                       <span>FULFILLMENT</span><ArrowRight size={13} />
                       <span>HISTORY</span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>


        {/* ====================================================
            TENANT POLICY
        ==================================================== */}
        <section className="siv6-section siv6-section--panel" id="tenant-policy">
          <div className="siv6-shell">
            <Reveal direction="bottomRight" className="siv6-section-head siv6-section-head--split">
              <div>
                <span className="siv6-kicker">PROMOTION RULES BY OPERATOR</span>
                <h2>Each promotion needs <span>its own defined conditions.</span></h2>
              </div>
              <p>
                An operator may run promotions with different eligibility windows, award types, entry limits, prize conditions and geographic availability. These distinctions belong in approved program settings and player disclosures. Changes to a live promotion should be reviewed before they affect existing entries.
              </p>
            </Reveal>

            <div className="siv6-tenant-map">
              <div className="siv6-tenant-map__grid" />

              <Reveal direction="left" className="siv6-tenant-node siv6-tenant-node--a">
                <span>OPERATOR A</span>
                <strong>PROMOTION TERMS</strong>
                <div><small>region</small><b>reviewed</b></div>
                <div><small>prizes</small><b>terms A</b></div>
                <div><small>awards</small><b>rules A</b></div>
                <div><small>entry</small><b>method A</b></div>
              </Reveal>

              <div className="siv6-tenant-line siv6-tenant-line--left"><i /><i /></div>

              <Reveal direction="up" className="siv6-tenant-core">
                <div className="siv6-tenant-core__rings"><i /><i /><i /></div>
                <Network size={27} />
                <span>ETHOSINO</span>
                <strong>SHARED CORE</strong>
                <small>shared product / defined promotions</small>
              </Reveal>

              <div className="siv6-tenant-line siv6-tenant-line--right"><i /><i /></div>

              <Reveal direction="right" className="siv6-tenant-node siv6-tenant-node--b">
                <span>OPERATOR B</span>
                <strong>PROMOTION TERMS</strong>
                <div><small>region</small><b>reviewed</b></div>
                <div><small>prizes</small><b>terms B</b></div>
                <div><small>awards</small><b>rules B</b></div>
                <div><small>entry</small><b>method B</b></div>
              </Reveal>
            </div>

            <Reveal direction="topRight" className="siv6-policy-principle">
              <Fingerprint size={22} />
              <div>
                <span>PROGRAM GOVERNANCE</span>
                <h3>The official rules should have one accountable owner.</h3>
                <p>
                  Before changing entry limits, promotional bonuses or prize requirements, the operator should confirm the effect on active participants and past entries. Ethosino can support recording program settings and change context; legal approval and communication remain operator responsibilities.
                </p>
              </div>
            </Reveal>
          </div>
        </section>


        {/* ====================================================
            FAIRNESS
        ==================================================== */}
        <section className="siv6-section" id="fairness">
          <div className="siv6-shell">
            <Reveal direction="topLeft" className="siv6-section-head siv6-section-head--split">
              <div>
                <span className="siv6-kicker">PROMOTIONAL GAMEPLAY & RESULT HISTORY</span>
                <h2>Game outcomes and prize eligibility <span>are different records.</span></h2>
              </div>
              <p>
                When promotional credits are used in a game, the resulting balance change should point to the correct event and title. Fairness verification helps explain supported game outcomes, while the promotion rules determine what those credits mean for prize eligibility. See the dedicated fairness page for the cryptographic methods and replay tools.
              </p>
            </Reveal>

            <div className="siv6-fairness-grid">
              <Reveal direction="bottomLeft" className="siv6-fairness-flow">
                <div className="siv6-fairness-flow__track" />
                {[
                  ["01", "BALANCE", "Identify whether the round used play-only or promotional credits."],
                   ["02", "GAME", "Record the game title, player action and resulting credit change."],
                   ["03", "HISTORY", "Keep the outcome linked to its promotional balance entry."],
                   ["04", "PRIZE", "Evaluate redemption separately under the approved program rules."]
                ].map(([num, label, text], index) => (
                  <motion.div
                    key={label}
                    className="siv6-fairness-step"
                    initial={reduceMotion ? false : { opacity: 0, y: index % 2 === 0 ? 45 : -45 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.09 }}
                  >
                    <span>{num}</span>
                    <strong>{label}</strong>
                    <p>{text}</p>
                  </motion.div>
                ))}

                <motion.i
                  className="siv6-fairness-packet"
                  animate={reduceMotion ? {} : { left: ["2%", "95%"] }}
                  transition={{ duration: 4.7, repeat: Infinity, ease: "linear" }}
                />
              </Reveal>

              <Reveal direction="topRight" className="siv6-verifier-terminal">
                <div className="siv6-verifier-terminal__head">
                  <Terminal size={14} /> promo.round.reference
                  <b>EXAMPLE</b>
                </div>
                <pre>{`$ inspect-promo-round --example

 tenant               sample_operator
 balance_type         promotional
 game                 example_first_party
 round_id             example_round_42
 activity_reference   recorded
 promo_ledger_entry   linked
 redemption_status    separate_review
 fairness_detail      see_fairness_page

 ✓ ILLUSTRATIVE RECORD RELATIONSHIP`}</pre>
                <div className="siv6-verifier-terminal__foot">
                  <Fingerprint size={15} />
                  A linked game event helps explain a promotional balance. Prize rights still depend on the program rules.
                </div>
              </Reveal>
            </div>
          </div>
        </section>


        {/* ====================================================
            DILIGENCE ROOM
        ==================================================== */}
        <section className="siv6-section siv6-section--panel" id="diligence">
          <div className="siv6-shell">
            <Reveal direction="bottomRight" className="siv6-section-head siv6-section-head--center">
              <span className="siv6-kicker">SWEEPSTAKES DEPLOYMENT CHECKLIST</span>
              <h2>Before launch, test <span>the entire promotional journey.</span></h2>
              <p>
                Use these scenarios with your legal and payment partners to establish which workflows are configured, demonstrated, or still depend on third parties.
              </p>
            </Reveal>

            <div className="siv6-question-grid">
              {diligenceQuestions.map((question, index) => (
                <Reveal
                  key={question}
                  direction={[
                    "topLeft",
                    "topRight",
                    "left",
                    "right",
                    "bottomLeft",
                    "bottomRight"
                  ][index]}
                  delay={index * 0.045}
                >
                  <Spotlight className="siv6-card siv6-question-card">
                    <span>0{index + 1}</span>
                    <ShieldCheck size={19} />
                    <h3>{question}</h3>
                    <small>VERIFY IN A SCOPED WALKTHROUGH.</small>
                  </Spotlight>
                </Reveal>
              ))}
            </div>
          </div>
        </section>


        {/* ====================================================
            LEGAL BOUNDARY
        ==================================================== */}
        <section className="siv6-section" id="boundary">
          <div className="siv6-shell">
            <Reveal direction="topLeft" className="siv6-boundary">
              <div className="siv6-boundary__copy">
                <span className="siv6-kicker">LEGAL & MARKET READINESS</span>
                <h2>A dual-currency design <span>does not establish legality.</span></h2>
                <p>
                  Sweepstakes-style casino products face different state restrictions and active regulatory scrutiny. No-purchase entry and separate coins are important considerations, but neither is a blanket legal exemption. The operator must obtain qualified advice for each intended market, keep its rules current and verify that its providers can support the approved deployment.
                </p>

                <div className="siv6-boundary__responsibilities">
                  {[
                    "Official rules, eligibility and promotional disclosures",
                     "Free-entry method and entry processing requirements",
                     "Markets approved for this particular program",
                     "Registrations or other permissions if applicable",
                     "Identity checks, prize providers and payout obligations",
                     "Rule changes, participant communications and counsel review"
                  ].map((item) => (
                    <div key={item}><Check size={13} />{item}</div>
                  ))}
                </div>
              </div>

              <div className="siv6-boundary__visual">
                <div className="siv6-boundary-box siv6-boundary-box--platform">
                  <Workflow size={24} />
                  <span>ETHOSINO</span>
                  <strong>PROGRAM TOOLING</strong>
                  <small>SET UP • REVIEW • TRACK</small>
                </div>

                <div className="siv6-boundary-not-equal">≠</div>

                <div className="siv6-boundary-box siv6-boundary-box--authority">
                  <Scale size={24} />
                  <span>LEGAL ELIGIBILITY</span>
                  <strong>OPERATOR + COUNSEL</strong>
                  <small>RULES • JURISDICTIONS • APPROVALS</small>
                </div>
              </div>
            </Reveal>
          </div>
        </section>


        {/* ====================================================
            FAQ
        ==================================================== */}
        <section className="siv6-section siv6-section--panel" id="faq">
          <div className="siv6-shell siv6-faq-layout">
            <Reveal direction="bottomLeft" className="siv6-faq-copy">
              <span className="siv6-kicker">SWEEPSTAKES QUESTIONS</span>
              <h2>Understand the two-coin model <span>before publishing the rules.</span></h2>
              <p>
                These answers cover what operators and players commonly misunderstand about free participation, promotional credits, purchase separation and prize fulfillment. They describe software scope, not legal approval for any market.
              </p>
              <Link className="siv6-button siv6-button--primary" to="/book-demo">
                Review the model together <ArrowRight size={16} />
              </Link>
            </Reveal>

            <Reveal direction="topRight" className="siv6-faq-list">
              {faqItems.map((item, index) => {
                const open = activeFaq === index;
                return (
                  <div key={item.q} className={`siv6-faq-item ${open ? "is-open" : ""}`}>
                    <button type="button" onClick={() => setActiveFaq(open ? -1 : index)}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{item.q}</strong>
                      <motion.i animate={{ rotate: open ? 180 : 0 }}><ChevronDown size={17} /></motion.i>
                    </button>
                    <AnimatePresence initial={false}>
                      {open ? (
                        <motion.div
                          className="siv6-faq-answer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.28 }}
                        >
                          <p>{item.a}</p>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                );
              })}
            </Reveal>
          </div>
        </section>


        {/* ====================================================
            FINAL CTA
        ==================================================== */}
        <section className="siv6-final" id="cta">
          <div className="siv6-shell">
            <Reveal direction="bottomRight" className="siv6-final-card">
              <div className="siv6-final-card__grid" />
              <div className="siv6-final-card__beam" />

              <div className="siv6-final-card__copy">
                <span className="siv6-kicker">DEPLOY THE PROGRAM YOU ACTUALLY APPROVED</span>
                <h2>Define the promotion. <span>Show players how it works.</span></h2>
                <p>
                  Bring your proposed Gold Coins/Sweeps Coins model, official rules, no-purchase entry method, eligible jurisdictions and planned payout providers. We can review how the player journey maps to Ethosino's available configuration, identify integration work and separate ready functionality from deployment-specific requirements.
                </p>

                <div className="siv6-final-card__actions">
                  <Link className="siv6-button siv6-button--primary siv6-button--large" to="/book-demo">
                    Discuss a sweepstakes implementation <ArrowRight size={18} />
                  </Link>
                  <a className="siv6-button siv6-button--secondary siv6-button--large" href="/compliance-overview.pdf">
                    <FileText size={17} /> Compliance overview
                  </a>
                </div>
              </div>

              <div className="siv6-final-card__proof">
                <span>SCOPE THESE BEFORE GO-LIVE</span>
                {[
                  "Gold Coins and Sweeps Coins wallet behavior",
                   "Free-entry instructions and intake handling",
                   "Promotional grants and source attribution",
                   "Prize claim review and payout dependencies",
                   "Approved promotion terms and eligible markets",
                   "Provider integrations and launch acceptance tests"
                ].map((item) => (
                  <div key={item}><BadgeCheck size={15} />{item}</div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </Fragment>
  );
};

export default SweepstakesInfrastructurePage;
